import { lookupService } from "dns";
import { CommonUtility } from "../../../Services/CommonUtility";
import LookupService from "../../../Services/LookupService";
import { SelectOption } from "../../../shared/components/input/SelectOption";
import { PolicyDto } from "../../model/PolicyDto";
import PolicyApi from "../../services/PolicyAPI";
import * as constant from '../../../shared/util/constant';
import CommonDataService from "../../../Services/CommonDataService";

class CustomerPolicyActionService {

    /**
     * Checks if a term with the same effective and expiration dates already exists for the same policy number.
     * For the same policy number, no two terms should have the same polEffDate and polExpDate.
     * @param policyNumber The policy number to check against.
     * @param allPolicies All policies for the customer.
     * @param newEffDate The effective date for the new policy term.
     * @param newExpDate The expiration date for the new policy term.
     * @returns { isDuplicate: boolean, errorText?: string }
     */
    public checkDuplicateTerm(policyNumber: string, allPolicies: PolicyDto[], newEffDate: Date, newExpDate: Date): { isDuplicate: boolean, errorText?: string } {
        if (!policyNumber || !allPolicies || allPolicies.length === 0) {
            return { isDuplicate: false };
        }
        
        const newEffDateStr = CommonUtility.datetimeToDateOnly(newEffDate);
        const newExpDateStr = CommonUtility.datetimeToDateOnly(newExpDate);
        
        // Filter policies with the same policy number
        const samePolicyNumberPolicies = allPolicies.filter(p => p.policyNumber === policyNumber);
        
        // Check each policy's polEffDate and polExpDate for duplicates
        for (const policy of samePolicyNumberPolicies) {
            const policyEffDate = policy.polEffDate ? CommonUtility.datetimeToDateOnly(policy.polEffDate) : null;
            const policyExpDate = policy.polExpDate ? CommonUtility.datetimeToDateOnly(policy.polExpDate) : null;
            
            if (policyEffDate && policyExpDate) {
                if (policyEffDate === newEffDateStr && policyExpDate === newExpDateStr) {
                    return {
                        isDuplicate: true,
                        errorText: constant.policyActionSummaryConstant.DuplicateTermOnRenewValidationErrror
                    };
                }
            }
        }
        
        return { isDuplicate: false };
    }
  
    policAPI: PolicyApi = new PolicyApi();
         
    savePolicyTransaction= async(transactionTypeCode: string, policyDto: PolicyDto) =>{
           const policyTransactionResponse = await this.policAPI.applyPolicyTransaction(transactionTypeCode, policyDto);
           return policyTransactionResponse;
         }

    deletePolicy = async(description: string, policyId: string, addActivity: boolean) =>{
           const policyTransactionResponse = await this.policAPI.deletePolicy(description, policyId, addActivity);
           return policyTransactionResponse;
         }
       
    fetchDropDownData = async () => {
 
        let policyBusinessTypeListList = await LookupService.fetchPolicBusinessType();
        let fetchUSStatesList = await LookupService.fetchUSStates();
        let stateUSOptions = fetchUSStatesList ? fetchUSStatesList.map(item => {
            return {
                label: item.lookupValue,
                value: item.lookupCode,
                isInactive: false
            }
        }) : [];
       
        let sourceTypes = await LookupService.fetchSourceType();
        let lobs = await LookupService.fetchLineofBusiness();
        let transactionTypes = await LookupService.fetchTransactionType();   
        let companyList = await CommonDataService.getCompanyList();
        let policyTypes = await LookupService.fetchPolicyType();
        let formTypes = await LookupService.fetchFormTypes();
       
        return {
            transactionTypeLookup: transactionTypes ? transactionTypes.map(item => {
            return {
                label: item.lookupValue,
                value: item.lookupCode || item.id,
                isInactive: false
            }}) : [],
            businessTypeLookup: CommonUtility.mapToSelectOptions(policyBusinessTypeListList),
            usStatesLookup: stateUSOptions,
            lineOfBusinessLookup: CommonUtility.mapToSelectOptions(lobs),
            sourceTypesLookup: CommonUtility.mapToSelectOptions(sourceTypes),
            parentCompanyListLookup: CommonUtility.mapCommonDataDtoToSelectOptions(companyList.filter(item => item.flag === true)), 
            writingCompaniesLookup: CommonUtility.mapCommonDataDtoToSelectOptions(companyList.filter(item => item.flag === false)),
            companyListLookup: companyList ? companyList.map(item => {
                return {
                    label: item.description,
                    value: item.id,
                    isInactive: false,
                    parentId: item.parentCompanyId
                }
            }) : [],
            policyTypeLookup: CommonUtility.mapToSelectOptions(policyTypes),
            formTypeLookup: CommonUtility.mapToSelectOptions(formTypes)
        };
   }   
   
   getCancellationReason = async() =>{
     let cancellationReasonList = await LookupService.fetchCancellationReason();     
     return CommonUtility.mapToSelectOptions(cancellationReasonList);    
   }

    getSourceTypeLookupValue = async (sourceTypeId: string): Promise<any> => {
        let sourceTypeTableId = await LookupService.fetchSourceTypeId(sourceTypeId);
        return (Array.isArray(sourceTypeTableId) && sourceTypeTableId.length > 0) ? sourceTypeTableId[0] : {};
    }

    getSourceList = async (sourceType: string): Promise<any> => {
        let sourceList = await this.policAPI.getSourceList(sourceType);
         return sourceList ? sourceList.map(item => {
            return {
                label: item.sourceName === undefined ? "": item.sourceName,
                value: item.id,
                isInactive: item.isActive
            }
        }) : [];
    }

    public validatePolicyCancel = (policyAction : PolicyDto, isCancel:boolean) : any => {
                    let validationErrors = {};       
            
                    if ((!policyAction.policyEvents[0].tranNote || policyAction.policyEvents[0].tranNote.trim().length == 0)) {
                        validationErrors['description'] = {isValid : false, warningText :  constant.policyActionSummaryConstant.Customer_Policy_Action_MANDATORY};
                    }
    
                    if ((!policyAction.polEffDate)) {                               
                        validationErrors['fromEffectiveDate'] = {isValid : false, warningText :  constant.policyActionSummaryConstant.Customer_Policy_Action_MANDATORY};
                    }

                    if ((isCancel && (!policyAction.policyEvents[0].cancellationReasonId || policyAction.policyEvents[0].cancellationReasonId.trim().length == 0))) {
                        validationErrors['selectedCancellationReason'] = {isValid : false, warningText :  constant.policyActionSummaryConstant.Customer_Policy_Action_MANDATORY};
                    }

                     if ((isCancel && (policyAction.policyTransactionPremiums[0].premium === undefined || policyAction.policyTransactionPremiums[0].premium === null))) {
                        validationErrors['premium'] = {isValid : false, warningText :  constant.policyActionSummaryConstant.Customer_Policy_Action_MANDATORY};
                    }
    
                     return validationErrors;
                }
    
    public validatePolicyAction = (policyAction : PolicyDto) : any => {
            let validationErrors = {};       
    
            if ((!policyAction.policyNumber || policyAction.policyNumber?.trim().length == 0)) {
                validationErrors['policyNumber'] = {isValid : false, warningText :  constant.policyActionSummaryConstant.Customer_Policy_Action_MANDATORY};
            }

             if (( policyAction.policyNumber?.trim().length < 2 )) {
                validationErrors['policyNumber'] = {isValid : false, warningText :  constant.policyActionSummaryConstant.PolicyNumber_MinLength};
            }

            if ((!policyAction.polTypeLobId || policyAction.polTypeLobId?.length == 0)) {
                validationErrors['lobDD'] = {isValid : false, warningText :  constant.policyActionSummaryConstant.Customer_Policy_Action_MANDATORY};
            }

            if ((!policyAction.formTypeId || policyAction.formTypeId?.trim().length == 0)) {
                validationErrors['formTypeId'] = {isValid : false, warningText :  constant.policyActionSummaryConstant.Customer_Policy_Action_MANDATORY};
            }

            if ((!policyAction.typeOfBusId || policyAction.typeOfBusId?.trim().length == 0)) {
                validationErrors['typeOfBusiness'] = {isValid : false, warningText :  constant.policyActionSummaryConstant.Customer_Policy_Action_MANDATORY};
            }         

           if ((!policyAction.issuedState || policyAction.issuedState?.trim().length == 0)) {
                validationErrors['issueStateDD'] = {isValid : false, warningText :  constant.policyActionSummaryConstant.Customer_Policy_Action_MANDATORY};
            }

            if ((!policyAction.sourceTypeId || policyAction.sourceTypeId?.trim().length == 0)) {
                validationErrors['sourceDD'] = {isValid : false, warningText :  constant.policyActionSummaryConstant.Customer_Policy_Action_MANDATORY};
            }

            if ((!policyAction.sourceId || policyAction.sourceId?.trim().length == 0)) {
                validationErrors['sourceIDDD'] = {isValid : false, warningText :  constant.policyActionSummaryConstant.Customer_Policy_Action_MANDATORY};
            }
           
            if ((!policyAction.polEffDate)) {
               
                validationErrors['fromEffectiveDate'] = {isValid : false, warningText :  constant.policyActionSummaryConstant.Customer_Policy_Action_MANDATORY};
            }

            if ((!policyAction.polExpDate)) {
                validationErrors['toEffectiveDate'] = {isValid : false, warningText :  constant.policyActionSummaryConstant.Customer_Policy_Action_MANDATORY};
            }            
            
            if (policyAction.polEffDate && policyAction.polExpDate && policyAction.polEffDate > policyAction.polExpDate) {
                    validationErrors['toEffectiveDate'] = { isValid: false, warningText: constant.policyActionSummaryConstant.Customer_Policy_Action_DATE_RANGE_INVALID };
            }

            if ((!policyAction.companyId || policyAction.companyId?.trim().length == 0)) {
                validationErrors['companyId'] = {isValid : false, warningText :  constant.policyActionSummaryConstant.Customer_Policy_Action_MANDATORY};
            }
            
            if ((!policyAction.writingCompanyId || policyAction.writingCompanyId?.trim().length == 0)) {
                validationErrors['writingCompanyId'] = {isValid : false, warningText :  constant.policyActionSummaryConstant.Customer_Policy_Action_MANDATORY};
            }  

            if (policyAction.fullTermPremium === undefined || policyAction.fullTermPremium === null || Number.isNaN(Number(policyAction.fullTermPremium))) {
                validationErrors['fullTermPremium'] = {isValid : false, warningText :  constant.policyActionSummaryConstant.Customer_Policy_Action_MANDATORY};
            }

            if ((!policyAction.policyTypeId || policyAction.policyTypeId?.trim().length == 0)) {
                validationErrors['policyTypeId'] = {isValid : false, warningText :  constant.policyActionSummaryConstant.Customer_Policy_Action_MANDATORY};
            }

            return validationErrors;
        }
}

export default CustomerPolicyActionService;